import { useEffect, useRef, useId } from 'react'

// Cloudflare's well-known "always passes" test site key — the correct fallback for local
// dev and tests, not a real key hardcoded into the repo. The real one comes from
// VITE_TURNSTILE_SITE_KEY at build time once the Cloudflare-side widget exists (see the
// onboarding wizard plan's Turnstile section and terraform/cloudflare); until then this
// makes signup work exactly as before, just with the widget visibly present.
const TEST_SITE_KEY = '1x00000000000000000000AA'
const SITE_KEY = import.meta.env.VITE_TURNSTILE_SITE_KEY || TEST_SITE_KEY

const SCRIPT_SRC = 'https://challenges.cloudflare.com/turnstile/v0/api.js'
let scriptLoadPromise: Promise<void> | null = null

function loadScript(): Promise<void> {
  if (scriptLoadPromise) return scriptLoadPromise
  scriptLoadPromise = new Promise((resolve, reject) => {
    if (document.querySelector(`script[src="${SCRIPT_SRC}"]`)) {
      resolve()
      return
    }
    const script = document.createElement('script')
    script.src = SCRIPT_SRC
    script.async = true
    script.defer = true
    script.onload = () => resolve()
    script.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(script)
  })
  return scriptLoadPromise
}

declare global {
  interface Window {
    turnstile?: {
      render: (container: HTMLElement, options: { sitekey: string; callback: (token: string) => void }) => string
      remove: (widgetId: string) => void
    }
  }
}

export function Turnstile({ onVerify }: { onVerify: (token: string) => void }) {
  const containerRef = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const id = useId()

  useEffect(() => {
    let cancelled = false
    loadScript().then(() => {
      if (cancelled || !containerRef.current || !window.turnstile) return
      widgetId.current = window.turnstile.render(containerRef.current, {
        sitekey: SITE_KEY,
        callback: onVerify,
      })
    })
    return () => {
      cancelled = true
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- onVerify is a per-render
    // closure in the caller; re-running this effect on every parent render would tear
    // down and re-mount the widget mid-solve, which is worse than the (harmless) stale
    // closure risk here.
  }, [])

  return <div ref={containerRef} id={`turnstile-${id}`} className="mb-4" />
}
